// ==================================================================================
// Digital Approval System (DAS) - Consolidation Service
// ==================================================================================
// Service functions for Two-Level Approval / Consolidation operations
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse } from '../../types/common.types';
import { Request } from '../../types/requests.types';
import {
  PreApproveRequest,
  PreApproveResponse,
  CreateConsolidatedRequestRequest,
  CreateConsolidatedRequestResponse,
  AddToConsolidationRequest,
  RemoveFromConsolidationRequest,
  ConsolidationUpdateResponse,
  FinalApproveRequest,
  FinalRejectRequest,
  FinalApprovalResponse,
  ConsolidatedRequestSummary,
  PreApprovedRequest,
  PreApprovedRequestQueryParameters,
  PreApprovedRequestListResponse,
  ConsolidatedRequestListResponse,
  TwoLevelApprovalStatistics,
  ValidateConsolidationRequest,
  ValidateConsolidationResponse,
  BooleanCheckResponse,
  CountResponse,
  UndoConsolidationRequest,
  UndoConsolidationResponse,
  ConsolidationWorkflowConfig,
} from '../../types/das/consolidation.types';
import { consolidationEndpoints } from '../../endpoints/das/consolidation.endpoints';

/**
 * Consolidation service for Two-Level Approval workflow
 */
export const consolidationService = {
  // ==================================================================================
  // PRE-APPROVAL OPERATIONS
  // ==================================================================================

  /**
   * Pre-approve a request (first level of two-level approval)
   */
  preApprove: async (requestId: string, request: PreApproveRequest): Promise<PreApproveResponse> => {
    const response = await axiosClient.post<ApiResponse<PreApproveResponse>>(
      consolidationEndpoints.preApprove(requestId),
      request
    );
    return response.data.data;
  },

  /**
   * Check if current user can pre-approve a request
   */
  canPreApprove: async (requestId: string): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.canPreApprove(requestId)
    );
    return response.data.data.canPreApprove ?? false;
  },

  /**
   * Get pre-approved requests available for consolidation
   */
  getPreApproved: async (
    params?: PreApprovedRequestQueryParameters
  ): Promise<PreApprovedRequestListResponse> => {
    const response = await axiosClient.get<ApiResponse<PreApprovedRequestListResponse>>(
      consolidationEndpoints.getPreApproved(params)
    );
    return response.data.data;
  },

  /**
   * Get pre-approved requests by request type
   */
  getPreApprovedByType: async (
    requestTypeId: string,
    excludeAlreadyConsolidated = true
  ): Promise<PreApprovedRequest[]> => {
    const response = await axiosClient.get<ApiResponse<PreApprovedRequest[]>>(
      consolidationEndpoints.getPreApprovedByType(requestTypeId, excludeAlreadyConsolidated)
    );
    return response.data.data;
  },

  // ==================================================================================
  // CONSOLIDATION CREATION & MANAGEMENT
  // ==================================================================================

  /**
   * Create a consolidated approval request
   */
  create: async (
    request: CreateConsolidatedRequestRequest
  ): Promise<CreateConsolidatedRequestResponse> => {
    const response = await axiosClient.post<ApiResponse<CreateConsolidatedRequestResponse>>(
      consolidationEndpoints.create(),
      request
    );
    return response.data.data;
  },

  /**
   * Get a consolidated request with all its child items
   */
  getById: async (id: string): Promise<ConsolidatedRequestSummary> => {
    const response = await axiosClient.get<ApiResponse<ConsolidatedRequestSummary>>(
      consolidationEndpoints.getById(id)
    );
    return response.data.data;
  },

  /**
   * Get all consolidated requests with optional status filter
   */
  getAll: async (
    status?: string,
    pageNumber = 1,
    pageSize = 20
  ): Promise<ConsolidatedRequestListResponse> => {
    const response = await axiosClient.get<ApiResponse<ConsolidatedRequestListResponse>>(
      consolidationEndpoints.getAll(status, pageNumber, pageSize)
    );
    return response.data.data;
  },

  /**
   * Get child requests for a consolidated request
   */
  getChildren: async (id: string): Promise<Request[]> => {
    const response = await axiosClient.get<ApiResponse<Request[]>>(
      consolidationEndpoints.getChildren(id)
    );
    return response.data.data;
  },

  /**
   * Add pre-approved requests to an existing consolidation
   */
  addItems: async (
    id: string,
    request: AddToConsolidationRequest
  ): Promise<ConsolidationUpdateResponse> => {
    const response = await axiosClient.post<ApiResponse<ConsolidationUpdateResponse>>(
      consolidationEndpoints.addItems(id),
      request
    );
    return response.data.data;
  },

  /**
   * Remove requests from a consolidation
   */
  removeItems: async (
    id: string,
    request: RemoveFromConsolidationRequest
  ): Promise<ConsolidationUpdateResponse> => {
    const response = await axiosClient.post<ApiResponse<ConsolidationUpdateResponse>>(
      consolidationEndpoints.removeItems(id),
      request
    );
    return response.data.data;
  },

  /**
   * Undo a consolidation - reverts all child requests back to Pre Approved status
   */
  undo: async (
    id: string,
    request: UndoConsolidationRequest
  ): Promise<UndoConsolidationResponse> => {
    const response = await axiosClient.post<ApiResponse<UndoConsolidationResponse>>(
      consolidationEndpoints.undo(id),
      request
    );
    return response.data.data;
  },

  /**
   * Check if current user can undo a consolidation
   */
  canUndo: async (id: string): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.canUndo(id)
    );
    return response.data.data.canUndo ?? false;
  },

  // ==================================================================================
  // FINAL APPROVAL OPERATIONS
  // ==================================================================================

  /**
   * Give final approval to a consolidated request
   */
  finalApprove: async (
    id: string,
    request: FinalApproveRequest
  ): Promise<FinalApprovalResponse> => {
    const response = await axiosClient.post<ApiResponse<FinalApprovalResponse>>(
      consolidationEndpoints.finalApprove(id),
      request
    );
    return response.data.data;
  },

  /**
   * Reject a consolidated request
   */
  finalReject: async (
    id: string,
    request: FinalRejectRequest
  ): Promise<FinalApprovalResponse> => {
    const response = await axiosClient.post<ApiResponse<FinalApprovalResponse>>(
      consolidationEndpoints.finalReject(id),
      request
    );
    return response.data.data;
  },

  /**
   * Check if current user can give final approval
   */
  canFinalApprove: async (id: string): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.canFinalApprove(id)
    );
    return response.data.data.canFinalApprove ?? false;
  },

  // ==================================================================================
  // VALIDATION OPERATIONS
  // ==================================================================================

  /**
   * Validate requests for consolidation
   */
  validate: async (
    request: ValidateConsolidationRequest
  ): Promise<ValidateConsolidationResponse> => {
    const response = await axiosClient.post<ApiResponse<ValidateConsolidationResponse>>(
      consolidationEndpoints.validate(),
      request
    );
    return response.data.data;
  },

  /**
   * Check if a request is a consolidated request
   */
  isConsolidated: async (requestId: string): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.isConsolidated(requestId)
    );
    return response.data.data.isConsolidated ?? false;
  },

  /**
   * Check if a request is part of a consolidation
   */
  isInConsolidation: async (requestId: string): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.isInConsolidation(requestId)
    );
    return response.data.data.isInConsolidation ?? false;
  },

  /**
   * Get the parent consolidated request for a child request
   */
  getParent: async (requestId: string): Promise<Request | null> => {
    try {
      const response = await axiosClient.get<ApiResponse<Request>>(
        consolidationEndpoints.getParent(requestId)
      );
      return response.data.data;
    } catch {
      return null;
    }
  },

  // ==================================================================================
  // STATISTICS OPERATIONS
  // ==================================================================================

  /**
   * Get statistics for two-level approval workflow
   */
  getStatistics: async (entityId?: string): Promise<TwoLevelApprovalStatistics> => {
    const response = await axiosClient.get<ApiResponse<TwoLevelApprovalStatistics>>(
      consolidationEndpoints.getStatistics(entityId)
    );
    return response.data.data;
  },

  /**
   * Get count of pre-approved requests awaiting consolidation
   */
  getPendingConsolidationCount: async (requestTypeId?: string): Promise<number> => {
    const response = await axiosClient.get<ApiResponse<CountResponse>>(
      consolidationEndpoints.getPendingConsolidationCount(requestTypeId)
    );
    return response.data.data.count;
  },

  /**
   * Get count of consolidated requests pending final approval
   */
  getPendingFinalApprovalCount: async (): Promise<number> => {
    const response = await axiosClient.get<ApiResponse<CountResponse>>(
      consolidationEndpoints.getPendingFinalApprovalCount()
    );
    return response.data.data.count;
  },

  // ==================================================================================
  // CONFIGURATION OPERATIONS
  // ==================================================================================

  /**
   * Get consolidation workflow configuration
   */
  getConfig: async (
    enterpriseId?: string,
    entityId?: string
  ): Promise<ConsolidationWorkflowConfig> => {
    const response = await axiosClient.get<ApiResponse<ConsolidationWorkflowConfig>>(
      consolidationEndpoints.getConfig(enterpriseId, entityId)
    );
    return response.data.data;
  },

  /**
   * Check if consolidation is required before final approval
   */
  isConsolidationRequired: async (
    enterpriseId?: string,
    entityId?: string
  ): Promise<boolean> => {
    const response = await axiosClient.get<ApiResponse<BooleanCheckResponse>>(
      consolidationEndpoints.isConsolidationRequired(enterpriseId, entityId)
    );
    return response.data.data.isRequired ?? false;
  },
};
